import React, { useState } from 'react';
import { Share2, Copy, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';

interface MessageShareMenuProps {
  userMessage: string;
  assistantMessage: string;
  className?: string;
}

export const MessageShareMenu: React.FC<MessageShareMenuProps> = ({
  userMessage,
  assistantMessage,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const formatQAPair = () => {
    return `Q: ${userMessage}\n\nA: ${assistantMessage}\n\n---\nShared from Strykr AI`;
  };

  const handleCopyQA = async () => {
    try {
      const formattedText = formatQAPair();
      await navigator.clipboard.writeText(formattedText);
      toast({
        title: "Copied!",
        description: "Q&A copied to clipboard",
        duration: 2000,
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Please try again",
        duration: 2000,
      });
    }
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(`${userMessage.slice(0, 100)}${userMessage.length > 100 ? '...' : ''}\n\nAnswered by Strykr AI`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    setIsOpen(false);
  };

  const handleShareLinkedIn = () => {
    const text = encodeURIComponent(`${userMessage.slice(0, 100)}${userMessage.length > 100 ? '...' : ''}\n\nAnswered by Strykr AI`);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&summary=${text}`, '_blank');
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-8 px-3 py-1 transition-opacity duration-200 hover:bg-white/10 text-white/70 hover:text-white ${className}`}
          title="Share this Q&A"
        >
          <Share2 className="h-3 w-3 mr-2" />
          <span className="text-xs">Share Strykr Insights</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuLabel className="text-xs">Share Q&A</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleCopyQA}>
          <Copy className="h-3 w-3 mr-2" />
          Copy Q&A
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleShareTwitter}>
          <Link className="h-3 w-3 mr-2" />
          Share on Twitter
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleShareLinkedIn}>
          <Link className="h-3 w-3 mr-2" />
          Share on LinkedIn
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}; 